/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { H2, P, TextBlock } from "@cd2/text"
import { Placeholder, Spinner, useInterval } from "@cd2/components"
import { IComponentProp } from "@cd2/tools"
import { useTheme } from "@cd2/theme"
import { pollingIntervals } from "../constants/pollingIntervals"

export type IStatus = "loading" | "reloading" | "noResults" | "noResultsInFilters" | "done"

interface IProps {
  status: IStatus
  children: React.ReactNode
  minWidth?: IComponentProp<typeof HorizontalOverflow, "minWidth">
  overflowType?: IComponentProp<typeof HorizontalOverflow, "overflowType">
  showTopScrollbar?: IComponentProp<typeof HorizontalOverflow, "showTopScrollbar">

  renderNoResults?(): React.ReactNode

  renderNoResultsInFilters?(): React.ReactNode
}

export function TableWrapper({
  status,
  renderNoResults,
  renderNoResultsInFilters,
  children,
  minWidth = 650,
  overflowType,
  showTopScrollbar,
}: IProps) {
  return (
    <HorizontalOverflow
      minWidth={minWidth}
      overflowType={overflowType}
      showTopScrollbar={showTopScrollbar}
    >
      <ReloadingOverlay isReloading={status === "reloading"}>
        {status === "loading" && <Placeholder.Table />}
        {status === "noResults" && (renderNoResults ? renderNoResults() : <NoResults />)}
        {status === "noResultsInFilters" &&
          (renderNoResultsInFilters ? (
            renderNoResultsInFilters()
          ) : (
            <NoResults
              noResultsNode={<P>There are no results to be displayed in your current filters</P>}
            />
          ))}
        {(status === "reloading" || status === "done") && children}
      </ReloadingOverlay>
    </HorizontalOverflow>
  )
}

interface IReloadingOverlayProps {
  isReloading?: boolean
  children: React.ReactNode
}

export function ReloadingOverlay({ isReloading, children }: IReloadingOverlayProps) {
  const { borderRadius } = useTheme()

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      {children}
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.25);
          transition: 0.3s;
          opacity: ${isReloading ? 1 : 0};
          pointer-events: ${isReloading ? "all" : "none"};
          border-radius: ${borderRadius}px;
          text-align: center;
        `}
      >
        <div
          css={css`
            position: absolute;
            top: calc(50% - 15px);
            left: calc(50% - 15px);
          `}
        >
          <Spinner size={60} />
        </div>
      </div>
    </div>
  )
}

interface INoResultsProps {
  noResultsNode?: React.ReactNode
}

export function NoResults({ noResultsNode }: INoResultsProps) {
  return (
    <div
      css={css`
        padding: 50px 16px;
        background: #f5f5f5;
      `}
    >
      <TextBlock size="small" align="center">
        <H2>No results 📦</H2>
        {noResultsNode ?? <P>There are no results to be displayed</P>}
      </TextBlock>
    </div>
  )
}

interface IOverflowTableProps {
  minWidth: number
  children: React.ReactNode
  overflowType?: "scroll" | "auto"
  showTopScrollbar?: boolean
}

export function HorizontalOverflow({
  minWidth,
  children,
  overflowType = "auto",
  showTopScrollbar,
}: IOverflowTableProps) {
  const [isFullyScrolled, setIsFullyScrolled] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const childrenRef = useRef<HTMLDivElement>(null)
  const fakeScrollbarRef = useRef<HTMLDivElement>(null)

  useInterval(() => handleContainerScroll(), pollingIntervals.horizontalOverflowScroll)

  const pseudoCommonCss = css`
    content: "";
    display: block;
    width: 1px;
    height: 100%;
    position: absolute;
    top: 0;
    transition: 0.1s;
    z-index: 1;
  `

  function handleContainerScroll() {
    if (containerRef.current) {
      const element = containerRef.current

      setIsOverflowing(element.scrollWidth > element.clientWidth)
      setIsFullyScrolled(
        element.scrollWidth <= element.scrollLeft + element.getBoundingClientRect().width,
      )
      setIsScrolled(element.scrollLeft > 0)

      if (fakeScrollbarRef.current) {
        const scrollbar = fakeScrollbarRef.current

        scrollbar.scrollLeft = element.scrollLeft
      }
    }
  }

  function handleScrollbarScroll() {
    if (fakeScrollbarRef.current) {
      const scrollbar = fakeScrollbarRef.current

      if (containerRef.current) {
        const element = containerRef.current
        element.scrollLeft = scrollbar.scrollLeft
      }
    }
  }

  useLayoutEffect(() => {
    handleContainerScroll()
  }, [minWidth])

  useEffect(() => {
    window.addEventListener("resize", handleContainerScroll)
    return () => {
      window.removeEventListener("resize", handleContainerScroll)
    }
  }, [])

  function findChildrenWidth() {
    if (childrenRef.current) {
      const element = childrenRef.current

      const tables = element.getElementsByTagName("table")

      let tableWidth = 0

      if (tables && tables[0]) {
        tableWidth = tables[0].getBoundingClientRect().width
      }

      return Math.max(element.getBoundingClientRect().width, tableWidth)
    }
  }

  return (
    <div
      css={css`
        position: relative;
        overflow: hidden;

        &:before {
          left: -1px;
          box-shadow: 4px 0 8px rgba(0, 0, 0, 0.5), 1px 0 4px rgba(0, 0, 0, 0.33);
          opacity: ${isScrolled ? 1 : 0};
          ${pseudoCommonCss};
        }

        &:after {
          right: -1px;
          box-shadow: -4px 0 8px rgba(0, 0, 0, 0.5), -1px 0 4px rgba(0, 0, 0, 0.33);
          opacity: ${isFullyScrolled ? 0 : 1};
          ${pseudoCommonCss};
        }
      `}
    >
      {showTopScrollbar && isOverflowing && (
        <div
          ref={fakeScrollbarRef}
          onScroll={handleScrollbarScroll}
          css={css`
            overflow-x: ${overflowType};
            overflow-y: hidden;
            max-width: 100%;
          `}
        >
          <div
            css={css`
              padding-top: 1px;
              min-width: ${minWidth}px;
            `}
            style={childrenRef.current ? { width: findChildrenWidth() } : undefined}
          />
        </div>
      )}
      <div
        ref={containerRef}
        onScroll={handleContainerScroll}
        css={css`
          max-width: 100%;
          overflow-x: ${overflowType};
          overflow-y: hidden;
          position: relative;

          table {
            width: calc(100% - 1px) !important; // Fixes weird css bug
          }
        `}
      >
        <div
          css={css`
            min-width: ${minWidth}px;
          `}
        >
          <div ref={childrenRef}>{children}</div>
        </div>
      </div>
    </div>
  )
}
